import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { apigetPublic } from '../../service/api';

// Keeps <head> tags correct during client-side SPA navigation. The server injects
// the right tags into the initial HTML (for crawlers); this keeps them in sync
// as the user navigates without full page loads. Tags rendered here carry the
// same data-rh ownership as the server-injected ones, so Helmet replaces rather
// than duplicates them.
//
// Skipped entirely on /dashboard/* (internal app, not indexed).

const cache = new Map(); // normalized path -> meta object

function normalize(pathname) {
  let p = (pathname || '/').split('?')[0].split('#')[0].toLowerCase();
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

export default function PageMetaSync() {
  const { pathname } = useLocation();
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const key = normalize(pathname);

    if (key.startsWith('/dashboard')) {
      setMeta(null);
    } else if (cache.has(key)) {
      setMeta(cache.get(key));
    } else {
      (async () => {
        const res = await apigetPublic(`seo/meta?path=${encodeURIComponent(key)}`);
        if (cancelled) return;
        if (res?.status === 200 && res.data?.meta) {
          cache.set(key, res.data.meta);
          setMeta(res.data.meta);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  if (!meta) return null;

  const ogTitle = meta.ogTitle || meta.title;
  const ogDescription = meta.ogDescription || meta.description;

  return (
    <Helmet>
      {meta.title && <title>{meta.title}</title>}
      {meta.description && <meta name="description" content={meta.description} />}
      {meta.canonicalUrl && <link rel="canonical" href={meta.canonicalUrl} />}
      {meta.noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Jet Home Loans" />
      {meta.canonicalUrl && <meta property="og:url" content={meta.canonicalUrl} />}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      {meta.ogImage && <meta property="og:image" content={meta.ogImage} />}
      <meta name="twitter:card" content={meta.twitterCard || 'summary_large_image'} />
      {ogTitle && <meta name="twitter:title" content={ogTitle} />}
      {ogDescription && <meta name="twitter:description" content={ogDescription} />}
      {meta.ogImage && <meta name="twitter:image" content={meta.ogImage} />}
    </Helmet>
  );
}
